import { request } from '@/shared/api/request'
import { etagHeaders, workItemPath } from './paths'

export interface GenerateAccepted {
  job_id: number
}

export type GenerateJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'

export interface TeachingGenerateJobProgress {
  done: number
  total: number
  /** Lecture notes or PPT outline snapshot for UI hydrate (no extra node poll). */
  pages?: Array<{ title?: string; text?: string; notes?: string; bullets?: string[] }>
}

export interface TeachingGenerateJob {
  job_id: number
  status: GenerateJobStatus | string
  kind?: string
  stage_n?: number | null
  error_code?: number | string
  error_message?: string
  /** Present for lecture_notes / lecture_notes_run_all while polling. */
  progress?: TeachingGenerateJobProgress
  follow_up?: {
    kind?: string
    generate_job_id: number
    status?: string
  }
}

export class GenerateJobFailedError extends Error {
  readonly job: TeachingGenerateJob

  constructor(job: TeachingGenerateJob) {
    super(job.error_message || `generate failed (${job.error_code ?? job.status})`)
    this.name = 'GenerateJobFailedError'
    this.job = job
  }
}

export async function pollGenerateJob(
  fetchJob: () => Promise<TeachingGenerateJob>,
  options?: {
    intervalMs?: number
    onStatus?: (job: TeachingGenerateJob) => void | Promise<void>
  },
): Promise<TeachingGenerateJob> {
  const interval = options?.intervalMs ?? 1500
  for (;;) {
    const job = await fetchJob()
    await options?.onStatus?.(job)
    if (job.status === 'succeeded') return job
    if (job.status === 'failed' || job.status === 'cancelled') {
      throw new GenerateJobFailedError(job)
    }
    if (interval > 0) {
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }
}

export function getGenerateJob(
  workItemId: number,
  jobId: number,
): Promise<TeachingGenerateJob> {
  return request.get(workItemPath(workItemId, `/generate-jobs/${jobId}`))
}

export interface StageGenerateOptions {
  avoid_content?: string
  force_text?: boolean
}

export function generateStage(
  workItemId: number,
  stageN: number,
  options?: StageGenerateOptions,
): Promise<GenerateAccepted> {
  const body =
    options && (options.avoid_content || options.force_text)
      ? {
          avoid_content: options.avoid_content,
          force_text: Boolean(options.force_text),
        }
      : undefined
  return request.post(workItemPath(workItemId, `/design/stages/${stageN}/generate`), body)
}
